// api.js 


import axios from "axios";

// Hosted backend API base
const API_BASE = "https://time-tracker-gx97.onrender.com/api";

// Axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper for safe API calls
const safeApi = async (promise) => {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    console.error("API ERROR:", err.response?.data || err.message);
    throw err.response?.data || err;
  }
};

// =======================================================
//                        AUTH
// =======================================================
export const loginUser = async (email, password) => {
  return safeApi(api.post("/auth/login", { email, password }));
};

export const registerUser = async (name, email, password) => {
  return safeApi(api.post("/auth/register", { name, email, password }));
};

// =======================================================
//                     WORKSPACES
// =======================================================
export const fetchWorkspaces = async (owner_id) => {
  const data = await safeApi(api.get(`/workspaces/admin/${owner_id}`));
  return data.workspaces;
};

export const createWorkspace = async (name, owner_id) => {
  return safeApi(api.post("/workspaces/create", { name, owner_id }));
};

export const fetchWorkspaceReports = async (workspace_id) => {
  return safeApi(api.get(`/reports/workspace/${workspace_id}`));
};

export const fetchWorkspaceMembers = async (workspace_id) => {
  return safeApi(api.get(`/workspaces/${workspace_id}/members`));
};

// =======================================================
//                  TIME ENTRIES
// =======================================================
export const fetchUserTimeEntries = async (user_id) => {
  const data = await safeApi(api.get(`/time-entries/user/${user_id}`));
  return data.entries || [];
};

export const fetchLiveEntry = async (user_id) => {
  try {
    const res = await api.get(`/time-entries/live/${user_id}`);
    return res.data.live || null;
  } catch {
    return null;
  }
};

// =======================================================
//                       PROJECTS
// =======================================================
export const createProject = async (workspace_id, name, description) => {
  return safeApi(
    api.post("/projects/create", { workspace_id, name, description })
  );
};

export const fetchProjects = async (workspace_id) => {
  return safeApi(api.get(`/projects/${workspace_id}`));
};

// =======================================================
//                         TASKS
// =======================================================
export const createTask = async (
  project_id,
  description,
  assigned_to,
  status,
  priority
) => {
  return safeApi(
    api.post("/tasks/create", {
      project_id,
      description,
      assigned_to: assigned_to || null,
      status,
      priority,
      start_date: null,
      due_date: null,
    })
  );
};

export const fetchTasks = async (project_id) => {
  return safeApi(api.get(`/tasks/${project_id}`));
};

// =======================================================
//                       MEMBERS / INVITES
// =======================================================
export const inviteMember = async (workspace_id, email, role) => {
  return safeApi(api.post("/members/invite", { workspace_id, email, role }));
};

export const acceptInvite = async (token, name, password) => {
  return safeApi(
    api.post("/members/accept-invite", { token, name, password })
  );
};

export const fetchWorkspaceMembersForTasks = async (workspace_id) => {
  const data = await safeApi(api.get(`/workspaces/${workspace_id}/members`));
  return Array.isArray(data) ? data : data.members || [];
};

export const fetchWorkspaceMembersForAdmin = async (workspace_id) => {
  return safeApi(api.get(`/workspaces/${workspace_id}/members`));
};

// =======================================================
// DASHBOARD / LIVE DATA
// =======================================================
export const fetchLiveMembersEntries = async () => {
  return safeApi(api.get(`/time-entries/live-members`));
};

export const fetchAdminDashboardSummary = async () =>
  safeApi(api.get("/time-entries/dashboard/summary/admin"));

export const fetchAdminTopApps = async () =>
  safeApi(api.get("/time-entries/dashboard/top-apps/admin"));

export const fetchAdminTeamActivity = async () =>
  safeApi(api.get(`/time-entries/team-activity/admin`));

// =======================================================
//                     SCREENSHOTS
// =======================================================

// Auto-screenshot settings
export const updateScreenshotInterval = async (workspace_id, screenshot_interval) => {
  return safeApi(
    api.post("/screenshots/interval", { workspace_id, screenshot_interval })
  );
};

export const getScreenshotInterval = async (workspace_id) => {
  return safeApi(api.get(`/screenshots/interval/${workspace_id}`));
};

export const getScreenshotStatus = async (workspace_id) => {
  return safeApi(api.get(`/screenshots/status/${workspace_id}`));
};

export const testAutoScreenshot = async (workspace_id) => {
  return safeApi(api.post("/screenshots/test-auto", { workspace_id }));
};

// Manual screenshot request (for admins)
export const requestScreenshot = async (user_id) => {
  return safeApi(api.post("/screenshots/request", { user_id }));
};

// =======================================================
//                       SETTINGS
// =======================================================

// UPDATED: fetchSettings - only get screenshot interval
export const fetchSettings = async (workspace_id) => {
  try {
    // Get screenshot interval (this endpoint exists)
    const screenshotSettings = await getScreenshotInterval(workspace_id);
    
    // Return only screenshot interval for now
    // Other settings will come from defaults
    return {
      screenshot_interval: screenshotSettings.screenshot_interval || 600,
      screenshot_enabled: screenshotSettings.is_enabled !== false,
      // Default values for other settings
      productivity_tracking: true,
      activity_monitoring: true,
      app_tracking: true,
      url_tracking: true,
      idle_time_threshold: 300,
      daily_report_email: true,
      weekly_summary_email: false,
      screenshot_quality: "medium",
      keystroke_logging: false,
      privacy_blur: false,
      work_hours_start: "09:00",
      work_hours_end: "17:00",
      tracking_days: ["mon", "tue", "wed", "thu", "fri"]
    };
  } catch (err) {
    console.error("Error fetching settings:", err);
    // Return defaults
    return {
      screenshot_interval: 600,
      screenshot_enabled: true,
      productivity_tracking: true,
      activity_monitoring: true,
      app_tracking: true,
      url_tracking: true,
      idle_time_threshold: 300,
      daily_report_email: true,
      weekly_summary_email: false,
      screenshot_quality: "medium",
      keystroke_logging: false,
      privacy_blur: false,
      work_hours_start: "09:00",
      work_hours_end: "17:00",
      tracking_days: ["mon", "tue", "wed", "thu", "fri"]
    };
  }
};

// UPDATED: updateSettings - ONLY update screenshot interval
export const updateSettings = async (workspace_id, settings) => {
  try {
    console.log("🎯 Updating settings for workspace:", workspace_id);
    
    // Only update screenshot interval (this endpoint exists)
    if (settings.screenshot_interval !== undefined) {
      console.log("📸 Updating screenshot interval to:", settings.screenshot_interval);
      const result = await updateScreenshotInterval(workspace_id, settings.screenshot_interval);
      console.log("✅ Screenshot interval updated:", result);
      
      // Return success message
      return {
        success: true,
        message: `Auto-screenshot interval set to ${settings.screenshot_interval} seconds`,
        data: result
      };
    }
    
    // For now, skip other settings since endpoint doesn't exist
    console.log("⚠️  Other settings skipped - /settings endpoint not available");
    
    return { 
      success: true, 
      message: "Screenshot interval updated (other settings require backend implementation)" 
    };
    
  } catch (err) {
    console.error("❌ Error updating settings:", err);
    throw err;
  }
};


export default api;